import { UserPlusIcon } from "@heroicons/react/24/outline";
import {
    CalendarDate,
    closedStatusCategoryKey,
    closedStatusWindowStart,
    CustomListItemId,
    CustomListKey,
    HomeCareLeadId,
    Maybe,
    parseEntityId,
    TenantPartnershipId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferProcedureInput, inferProcedureOutput } from "@trpc/server";
import { PageHeader } from "components/layout";
import { OutgoingSharesIndicator } from "components/shared-pipelines/outgoing-shares-indicator";
import { SharedChip } from "components/shared-pipelines/shared-chip";
import {
    SharedPipelineBoard,
    SharedPipelineLoadError,
    SharedPipelineUnavailable,
} from "components/shared-pipelines/shared-pipeline-board";
import {
    homeCarePipelineTabs,
    homeCarePipelineView,
    HomeCarePipelineView,
    ownHomeCarePipelineTabKey,
} from "components/shared-pipelines/shared-pipeline-tabs";
import {
    SharedPipeline,
    useOutgoingPipelineShares,
    useSharedPipelines,
} from "components/shared-pipelines/use-shared-pipelines";
import { PipelineTabs } from "components/ui/pipeline-tabs";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import { produce } from "immer";
import * as React from "react";
import { useHistory, useParams } from "react-router-dom";
import { useCustomList } from "store/selectors/bridge";
import {
    useFeature,
    usePipelineState,
    useRegionId,
    useUsers,
} from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { handleEvent } from "util/user-events";
import { ClientSearch } from "../clients/client-search/client-search";
import { trpc, trpcClient } from "../rpc/trpc";
import { HomeCareLeadCard } from "./home-care-lead-card";
import { PipelineInner } from "./pipeline-inner";
import type { PipelineCardRenderProps, PipelineConfig, PipelineItem } from "./types";

type HomeCareLeadListResult = inferProcedureOutput<
    AppRouter["homeCareLead"]["listSummary"]
>;
type HomeCareLeadOutput = HomeCareLeadListResult["leads"][number];
type HomeCareLeadListInput = inferProcedureInput<
    AppRouter["homeCareLead"]["listSummary"]
>;

const homeCareLeadCardType = "HomeCareLeadCard" as const;

function homeCareLeadListInput(
    searchCriteria: Record<string, unknown>,
    statusId: CustomListItemId,
    statusChangedAfter: string | null,
): HomeCareLeadListInput {
    return {
        ...searchCriteria,
        statusListItemId: statusId,
        statusChangedAfter,
    } as HomeCareLeadListInput;
}

function useClosedStatusCutoff(): (statusId: CustomListItemId) => string | null {
    const { itemHasCategory } = useCustomList(CustomListKey.HomeCareLeadStatus);
    const { settings } = useTenantSettings();
    const closedPaidDuration = settings.closedPaidDuration;
    return React.useCallback(
        (statusId: CustomListItemId) => {
            if (!itemHasCategory(statusId, closedStatusCategoryKey)) {
                return null;
            }
            return closedStatusWindowStart(closedPaidDuration).toString();
        },
        [itemHasCategory, closedPaidDuration],
    );
}

function useHomeCareLeadColumnQuery(
    statusId: CustomListItemId,
    searchCriteria: Record<string, unknown>,
): { data: PipelineItem[] | undefined } {
    const trpcContext = trpc.useTRPC();
    const closedStatusCutoff = useClosedStatusCutoff();
    const query = useQuery({
        ...trpcContext.homeCareLead.listSummary.queryOptions(
            homeCareLeadListInput(searchCriteria, statusId, closedStatusCutoff(statusId)),
        ),
    });
    return {
        data: query.data ? (query.data.leads as PipelineItem[]) : undefined,
    };
}

const HomeCareLeadPipelineCard: React.FC<{
    client: HomeCareLeadOutput;
    highlightCard: boolean;
    pendingCard: boolean;
    showAssignedTo: boolean;
    getClientStatus: (item: PipelineItem) => {
        descriptor: string;
        date: Maybe<CalendarDate>;
    };
}> = props => {
    const users = useUsers().users;
    const assignedUser = props.client.assignedUserId
        ? users[props.client.assignedUserId]
        : null;

    return (
        <HomeCareLeadCard
            lead={{
                href: `/home-care-clients/show/${props.client.id}`,
                name: props.client.name,
                bestContactName: props.client.bestContactName,
                nextTask: props.client.nextTask,
                assignedUserName: assignedUser ? assignedUser.name : null,
                status: props.getClientStatus(props.client as unknown as PipelineItem),
            }}
            highlightCard={props.highlightCard}
            pendingCard={props.pendingCard}
            showAssignedTo={props.showAssignedTo}
        />
    );
};

function useHomeCareLeadPipelineConfig(): PipelineConfig {
    const queryClient = useQueryClient();
    const closedStatusCutoff = useClosedStatusCutoff();

    const onDragDrop = React.useCallback(
        async (
            entityId: string,
            oldStatusId: CustomListItemId,
            newStatusId: CustomListItemId,
            searchCriteria: Record<string, unknown>,
        ) => {
            handleEvent("home-care-client-status-change", {
                source: "pipeline",
            });

            const oldQueryKey = [
                ["homeCareLead", "listSummary"],
                {
                    input: homeCareLeadListInput(
                        searchCriteria,
                        oldStatusId,
                        closedStatusCutoff(oldStatusId),
                    ),
                    type: "query",
                },
            ];
            const oldQueryData: HomeCareLeadListResult | undefined =
                queryClient.getQueryData(oldQueryKey);
            const oldClient = oldQueryData?.leads.find(c => c.id === entityId);

            if (oldClient) {
                queryClient.setQueryData(
                    oldQueryKey,
                    (oldStatusData: HomeCareLeadListResult) => {
                        return produce(oldStatusData, (draft: HomeCareLeadListResult) => {
                            const index = draft.leads.findIndex(c => c.id === entityId);
                            if (index !== -1) {
                                draft.leads.splice(index, 1);
                                draft.total -= 1;
                            }
                        });
                    },
                );
                const newQueryKey = [
                    ["homeCareLead", "listSummary"],
                    {
                        input: homeCareLeadListInput(
                            searchCriteria,
                            newStatusId,
                            closedStatusCutoff(newStatusId),
                        ),
                        type: "query",
                    },
                ];
                queryClient.setQueryData(
                    newQueryKey,
                    (newStatusData: HomeCareLeadListResult) => {
                        return produce(newStatusData, (draft: HomeCareLeadListResult) => {
                            draft.leads.unshift(oldClient);
                            draft.total += 1;
                        });
                    },
                );
            }

            await trpcClient.homeCareLead.update.mutate({
                id: entityId as HomeCareLeadId,
                statusListItemId: newStatusId,
            });

            queryClient.refetchQueries({
                queryKey: [["homeCareLead", "listSummary"]],
            });
        },
        [queryClient, closedStatusCutoff],
    );

    const renderCard = React.useCallback(
        (item: PipelineItem, renderProps: PipelineCardRenderProps) => (
            <HomeCareLeadPipelineCard
                client={item as unknown as HomeCareLeadOutput}
                highlightCard={renderProps.highlightCard}
                pendingCard={renderProps.pendingCard}
                showAssignedTo={renderProps.showAssignedTo}
                getClientStatus={renderProps.getStatusDescription}
            />
        ),
        [],
    );

    return React.useMemo(
        () => ({
            customListKey: CustomListKey.HomeCareLeadStatus,
            cardType: homeCareLeadCardType,
            useColumnQuery: useHomeCareLeadColumnQuery,
            onDragDrop,
            renderCard,
        }),
        [onDragDrop, renderCard],
    );
}

const AddHomeCareLeadButton: React.FC = () => {
    const { push } = useHistory();
    const regionId = useRegionId();
    const [isCreating, setIsCreating] = React.useState(false);

    const onClick = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            if (!regionId || isCreating) return;
            setIsCreating(true);
            handleEvent("create-home-care-client", { source: "button" });
            try {
                const result = await trpcClient.homeCareLead.create.mutate({
                    name: "",
                    regionId,
                });
                push(`/home-care-clients/show/${result.id}`);
            } finally {
                setIsCreating(false);
            }
        },
        [regionId, isCreating, push],
    );

    if (!regionId) {
        return null;
    }

    return (
        <PrimaryButton onClick={onClick} disabled={isCreating}>
            <div className="flex items-center space-x-2">
                <UserPlusIcon className="w-5 h-5" />
                <span className="text-sm">New home care client</span>
            </div>
        </PrimaryButton>
    );
};

const OutgoingShares: React.FC = () => (
    <OutgoingSharesIndicator shares={useOutgoingPipelineShares()} />
);

const HomeCareLeadBoard: React.FC = () => {
    const config = useHomeCareLeadPipelineConfig();
    const pipelineState = usePipelineState();

    return (
        <div className="pipeline-wrapper bg-brand-700 flex px-4 py-2 flex-1 h-screen md:h-auto md:overflow-hidden">
            {pipelineState && pipelineState.isReady ? (
                <PipelineInner
                    config={config}
                    searchCriteria={pipelineState.searchCriteria}
                />
            ) : null}
        </div>
    );
};

type PipelineView = HomeCarePipelineView<SharedPipeline>;

const HomeCarePipelineHeader: React.FC<{ view: PipelineView }> = ({ view }) => {
    switch (view.kind) {
        case "own":
            return (
                <PageHeader
                    title="Home Care Pipeline"
                    titleAccessory={<OutgoingShares />}>
                    <AddHomeCareLeadButton />
                </PageHeader>
            );
        case "shared":
            return (
                <PageHeader
                    title={`${view.pipeline.sendingTenantName} Home Care Pipeline`}
                    titleAccessory={<SharedChip label="Shared with you" />}
                />
            );
        case "loading":
        case "unavailable":
        case "error":
            return <PageHeader title="Shared Home Care Pipeline" />;
        default: {
            const exhaustiveCheck: never = view;
            throw new Error(`Unhandled view: ${exhaustiveCheck}`);
        }
    }
};

const HomeCarePipelineContent: React.FC<{ view: PipelineView }> = ({ view }) => {
    switch (view.kind) {
        case "own":
            return <HomeCareLeadBoard />;
        case "shared":
            return <SharedPipelineBoard pipeline={view.pipeline} />;
        case "loading":
            return (
                <div className="flex items-center justify-center p-8">
                    <Spinner />
                </div>
            );
        case "unavailable":
            return <SharedPipelineUnavailable />;
        case "error":
            return <SharedPipelineLoadError />;
        default: {
            const exhaustiveCheck: never = view;
            throw new Error(`Unhandled view: ${exhaustiveCheck}`);
        }
    }
};

export const HomeCareLeadPipeline: React.FC = () => {
    const isEnabled = useFeature("homeCarePrototype");
    const { partnershipId } = useParams<{ partnershipId?: string }>();
    const shared = useSharedPipelines();

    if (!isEnabled) {
        return null;
    }

    const view = homeCarePipelineView(
        partnershipId ? parseEntityId<TenantPartnershipId>(partnershipId) : null,
        shared,
    );
    const tabs = homeCarePipelineTabs(shared.pipelines);

    return (
        <div className="flex flex-col md:inset-0 md:absolute">
            <HomeCarePipelineHeader view={view} />
            {view.kind === "own" || view.kind === "shared" ? (
                <div className="px-4 py-2">
                    <ClientSearch
                        includeCardSort
                        variant={view.kind === "shared" ? "sharedPipeline" : "clients"}
                    />
                </div>
            ) : null}
            <PipelineTabs
                tabs={tabs}
                selectedKey={partnershipId ?? ownHomeCarePipelineTabKey}
            />
            <HomeCarePipelineContent view={view} />
        </div>
    );
};
